import React, { useEffect, useMemo, useState } from 'react';
import { apiBaseUrl, services } from '../data';
import { parseApiResponse } from '../utils/api';
import './AdminPage.css';

/* ─── Constants ──────────────────────────────────────────── */

const initialJobForm = {
  title: '',
  department: '',
  location: 'Bengaluru',
  employment_type: 'Full Time',
  experience: '',
  summary: '',
  responsibilities: '',
  requirements: '',
  status: 'draft',
};

const adminSections = [
  { key: 'overview', label: 'Overview' },
  { key: 'create-job', label: 'Create Job' },
  { key: 'jobs', label: 'Job Postings' },
  { key: 'applications-all', label: 'Applications' },
];

const applicationFieldGroups = [
  {
    title: 'Contact',
    fields: [
      ['Mobile', 'phone_number'],
      ['Alternate', 'alternate_phone_number'],
      ['Email', 'email'],
      ['Location', 'current_location'],
    ],
  },
  {
    title: 'Experience',
    fields: [
      ['Total', 'total_experience'],
      ['Relevant', 'relevant_experience'],
      ['Skill', 'skills_trained'],
      ['Current Skill', 'current_skill'],
      ['New Skill', 'new_skill'],
      ['Role', 'role'],
    ],
  },
  {
    title: 'Education',
    fields: [
      ['Post Graduation', 'post_graduation'],
      ['Graduation', 'graduation'],
      ['12th / Diploma', 'twelfth_standard'],
      ['SSC', 'ssc'],
      ['Education Gaps', 'education_gaps'],
      ['Year Passed Out', 'year_passed_out'],
    ],
  },
  {
    title: 'Work',
    fields: [
      ['Company', 'current_company'],
      ['Previous', 'previous_companies'],
      ['Client Company', 'company_name'],
      ['Last Working Day', 'last_working_day'],
      ['Notice', 'notice_period'],
      ['Current CTC', 'current_ctc'],
      ['Expected CTC', 'expected_ctc'],
      ['Preferred', 'preferred_location'],
      ['Employment Gaps', 'employment_gaps'],
    ],
  },
  {
    title: 'Documentation',
    fields: [
      ['PAN Number', 'pan_number'],
      ['Date of Birth', 'date_of_birth'],
      ['Permanent Address', 'permanent_address'],
      ['Gender', 'gender'],
      ['Qualification', 'qualification'],
      ['Offer Date', 'offer_date'],
      ['Joining Date', 'joining_date'],
      ['Hike Date', 'hike_date'],
      ['Joining CTC', 'joining_ctc'],
      ['Joining Designation', 'joining_designation'],
      ['Current Designation', 'current_designation'],
      ['Resignation Date', 'resignation_date'],
      ['Relieving Date', 'relieving_date'],
      ['Technology / Course Name', 'technology_course_name'],
      ['Bank Name', 'bank_name'],
      ['Bank Account Number', 'bank_account_number'],
    ],
  },
  {
    title: 'Support',
    fields: [
      ['Job Description', 'job_description'],
      ['Timings', 'timings'],
      ['Duration', 'duration'],
      ['Exam Name', 'exam_name'],
      ['Exam Code', 'exam_code'],
      ['Exam Date', 'exam_date'],
      ['Exam Time', 'exam_time'],
      ['Interview Date', 'interview_date'],
      ['Interview Time', 'interview_time'],
      ['Intern Start', 'intern_start_date'],
      ['Intern End', 'intern_end_date'],
      ['Payroll Start', 'payroll_start_date'],
      ['Payroll End', 'payroll_end_date'],
      ['UAN', 'uan_number'],
      ['Consultant', 'consultant_name'],
      ['Comments', 'comments'],
    ],
  },
];

const documentLinks = [
  ['Aadhaar', 'aadhaar_card_file'],
  ['PAN', 'pan_card_file'],
  ['Resume', 'resume_file'],
  ['UAN Passbook', 'uan_passbook_file'],
  ['Bank Passbook', 'cheque_bank_passbook_file'],
  ['Bank Statement', 'bank_statement_file'],
];

const supportApplicationFieldGroups = {
  'Freshers Job Support': [
    {
      title: 'Candidate',
      fields: [
        ['Full Name', 'full_name'],
        ['Mobile', 'phone_number'],
        ['Alternate Mobile', 'alternate_phone_number'],
        ['Email', 'email'],
        ['Location', 'current_location'],
        ['Service', 'service_interest'],
        ['Total Experience', 'total_experience'],
        ['Consultant Name', 'consultant_name'],
      ],
    },
    {
      title: 'Education',
      fields: [
        ['Post Graduation', 'post_graduation'],
        ['Graduation', 'graduation'],
        ['12th / Diploma', 'twelfth_standard'],
        ['SSC', 'ssc'],
        ['Education Gaps', 'education_gaps'],
        ['Skills Trained', 'skills_trained'],
      ],
    },
  ],
  'Lateral Job Support': [
    {
      title: 'Candidate',
      fields: [
        ['Full Name', 'full_name'],
        ['Mobile', 'phone_number'],
        ['Alternate Mobile', 'alternate_phone_number'],
        ['Email', 'email'],
        ['Password', 'candidate_password'],
        ['Location', 'current_location'],
      ],
    },
    {
      title: 'Lateral Details',
      fields: [
        ['Total Experience', 'total_experience'],
        ['Relevant Experience', 'relevant_experience'],
        ['Skill', 'skills_trained'],
        ['Last Working Day', 'last_working_day'],
        ['Notice Period', 'notice_period'],
        ['Current Company', 'current_company'],
        ['Previous Companies', 'previous_companies'],
        ['Current CTC', 'current_ctc'],
        ['Expected CTC', 'expected_ctc'],
        ['Preferred Location', 'preferred_location'],
        ['Employment Gaps', 'employment_gaps'],
        ['UAN No', 'uan_number'],
        ['Comments', 'comments'],
        ['Consultant Name', 'consultant_name'],
      ],
    },
  ],
  'Open Job Support': [
    {
      title: 'Candidate',
      fields: [
        ['Full Name', 'full_name'],
        ['Mobile', 'phone_number'],
        ['Alternate Mobile', 'alternate_phone_number'],
        ['Email', 'email'],
        ['Password', 'candidate_password'],
        ['Location', 'current_location'],
      ],
    },
    {
      title: 'Open Job Details',
      fields: [
        ['Total Experience', 'total_experience'],
        ['Relevant Experience', 'relevant_experience'],
        ['Current Skill', 'current_skill'],
        ['New Skill', 'new_skill'],
        ['Last Working Day', 'last_working_day'],
        ['Notice Period', 'notice_period'],
        ['Current Company', 'current_company'],
        ['Previous Companies', 'previous_companies'],
        ['Current CTC', 'current_ctc'],
        ['Expected CTC', 'expected_ctc'],
        ['Preferred Location', 'preferred_location'],
        ['Employment Gaps', 'employment_gaps'],
        ['UAN No', 'uan_number'],
        ['Comments', 'comments'],
        ['Consultant Name', 'consultant_name'],
      ],
    },
  ],
  'Technical Job Support': [
    {
      title: 'Technical Support Details',
      fields: [
        ['Full Name', 'full_name'],
        ['Mobile', 'phone_number'],
        ['Alternate Mobile', 'alternate_phone_number'],
        ['Email', 'email'],
        ['Password', 'candidate_password'],
        ['Location', 'current_location'],
        ['Total Experience', 'total_experience'],
        ['Role', 'role'],
        ['Job Description', 'job_description'],
        ['Company Name', 'company_name'],
        ['Timings', 'timings'],
        ['Duration', 'duration'],
        ['Consultant Name', 'consultant_name'],
      ],
    },
  ],
  'Certification Support': [
    {
      title: 'Certification Details',
      fields: [
        ['Full Name', 'full_name'],
        ['Mobile', 'phone_number'],
        ['Alternate Mobile', 'alternate_phone_number'],
        ['Email', 'email'],
        ['Exam Name', 'exam_name'],
        ['Exam Code', 'exam_code'],
        ['Exam Date', 'exam_date'],
        ['Exam Time', 'exam_time'],
        ['Consultant Name', 'consultant_name'],
      ],
    },
  ],
  'Interview Support': [
    {
      title: 'Interview Details',
      fields: [
        ['Full Name', 'full_name'],
        ['Mobile', 'phone_number'],
        ['Alternate Mobile', 'alternate_phone_number'],
        ['Email', 'email'],
        ['Password', 'candidate_password'],
        ['Role', 'role'],
        ['Job Description', 'job_description'],
        ['Total Experience', 'total_experience'],
        ['Relevant Experience', 'relevant_experience'],
        ['Interview Date', 'interview_date'],
        ['Interview Time', 'interview_time'],
        ['Comments', 'comments'],
        ['Company Name', 'company_name'],
        ['Location', 'current_location'],
        ['Consultant Name', 'consultant_name'],
      ],
    },
  ],
  'Training Support': [
    {
      title: 'Training Details',
      fields: [
        ['Full Name', 'full_name'],
        ['Mobile', 'phone_number'],
        ['Alternate Mobile', 'alternate_phone_number'],
        ['Email', 'email'],
        ['Year Of Passed Out', 'year_passed_out'],
        ['Skill', 'skills_trained'],
        ['Location', 'current_location'],
        ['Consultant Name', 'consultant_name'],
      ],
    },
  ],
  'Internship Support': [
    {
      title: 'Internship Details',
      fields: [
        ['Full Name', 'full_name'],
        ['Mobile', 'phone_number'],
        ['Alternate Mobile', 'alternate_phone_number'],
        ['Email', 'email'],
        ['Skill', 'skills_trained'],
        ['Location', 'current_location'],
        ['Year Of Passed Out', 'year_passed_out'],
        ['Intern Start Date', 'intern_start_date'],
        ['Intern End Date', 'intern_end_date'],
        ['Consultant Name', 'consultant_name'],
      ],
    },
  ],
  'Payroll Support': [
    {
      title: 'Payroll Details',
      fields: [
        ['Full Name', 'full_name'],
        ['Mobile', 'phone_number'],
        ['Alternate Mobile', 'alternate_phone_number'],
        ['Email', 'email'],
        ['Total Experience', 'total_experience'],
        ['Location', 'current_location'],
        ['UAN Number', 'uan_number'],
        ['Payroll Start Date', 'payroll_start_date'],
        ['Payroll End Date', 'payroll_end_date'],
        ['Consultant Name', 'consultant_name'],
      ],
    },
  ],
  'Documentation Support': [
    {
      title: 'Document Support',
      fields: [
        ['Full Name', 'full_name'],
        ['Mobile', 'phone_number'],
        ['Alternate Mobile', 'alternate_phone_number'],
        ['Mail ID', 'email'],
        ['PAN Number', 'pan_number'],
        ['Date of Birth', 'date_of_birth'],
        ['Permanent Address', 'permanent_address'],
        ['Gender', 'gender'],
        ['Qualification', 'qualification'],
        ['Offer Date', 'offer_date'],
        ['Joining Date', 'joining_date'],
        ['Hike Date', 'hike_date'],
        ['Joining CTC', 'joining_ctc'],
        ['Current CTC', 'current_ctc'],
        ['Joining Designation', 'joining_designation'],
        ['Current Designation', 'current_designation'],
        ['Resignation Date', 'resignation_date'],
        ['Relieving Date', 'relieving_date'],
        ['Technology / Course Name', 'technology_course_name'],
        ['Bank Name', 'bank_name'],
        ['Bank Account Number', 'bank_account_number'],
        ['UAN No', 'uan_number'],
        ['Consultant Name', 'consultant_name'],
      ],
    },
  ],
};

const supportDocumentLinks = {
  'Freshers Job Support': [
    ['Aadhaar', 'aadhaar_card_file'],
    ['PAN', 'pan_card_file'],
    ['Resume', 'resume_file'],
  ],
  'Lateral Job Support': [
    ['Resume', 'resume_file'],
    ['Aadhaar', 'aadhaar_card_file'],
    ['PAN Card', 'pan_card_file'],
    ['UAN Passbook', 'uan_passbook_file'],
  ],
  'Open Job Support': [
    ['Resume', 'resume_file'],
    ['Aadhaar', 'aadhaar_card_file'],
    ['PAN Card', 'pan_card_file'],
    ['UAN Passbook', 'uan_passbook_file'],
  ],
  'Training Support': [
    ['Aadhaar', 'aadhaar_card_file'],
  ],
  'Internship Support': [
    ['Cheque / Bank Passbook', 'cheque_bank_passbook_file'],
  ],
  'Payroll Support': [
    ['Aadhaar', 'aadhaar_card_file'],
    ['Cheque / Bank Passbook', 'cheque_bank_passbook_file'],
  ],
  'Documentation Support': [
    ['Aadhaar Attachment', 'aadhaar_card_file'],
    ['PAN Card Attachment', 'pan_card_file'],
    ['Bank 3 Months Statement', 'bank_statement_file'],
  ],
};

const exportColumns = [
  ['Application ID', 'fjs_id'],
  ['Name', 'full_name'],
  ['Service', 'service_interest'],
  ['Job', 'job'],
  ['Status', 'status'],
  ['Mobile', 'phone_number'],
  ['Alternate Mobile', 'alternate_phone_number'],
  ['Email', 'email'],
  ['Location', 'current_location'],
  ['Total Experience', 'total_experience'],
  ['Relevant Experience', 'relevant_experience'],
  ['Skill', 'skills_trained'],
  ['Current Skill', 'current_skill'],
  ['New Skill', 'new_skill'],
  ['Post Graduation', 'post_graduation'],
  ['Graduation', 'graduation'],
  ['12th / Diploma', 'twelfth_standard'],
  ['SSC', 'ssc'],
  ['Education Gaps', 'education_gaps'],
  ['Year Passed Out', 'year_passed_out'],
  ['Role', 'role'],
  ['Job Description', 'job_description'],
  ['Current Company', 'current_company'],
  ['Previous Companies', 'previous_companies'],
  ['Company Name', 'company_name'],
  ['Notice Period', 'notice_period'],
  ['PAN Number', 'pan_number'],
  ['Date of Birth', 'date_of_birth'],
  ['Permanent Address', 'permanent_address'],
  ['Gender', 'gender'],
  ['Qualification', 'qualification'],
  ['Offer Date', 'offer_date'],
  ['Joining Date', 'joining_date'],
  ['Hike Date', 'hike_date'],
  ['Joining CTC', 'joining_ctc'],
  ['Joining Designation', 'joining_designation'],
  ['Current Designation', 'current_designation'],
  ['Resignation Date', 'resignation_date'],
  ['Relieving Date', 'relieving_date'],
  ['Technology / Course Name', 'technology_course_name'],
  ['Bank Name', 'bank_name'],
  ['Bank Account Number', 'bank_account_number'],
  ['Current CTC', 'current_ctc'],
  ['Expected CTC', 'expected_ctc'],
  ['Preferred Location', 'preferred_location'],
  ['UAN', 'uan_number'],
  ['Consultant', 'consultant_name'],
  ['Comments', 'comments'],
  ['Admin Notes', 'admin_notes'],
  ['Submitted At', 'submitted_at'],
];

/* ─── Helpers ────────────────────────────────────────────── */

const normalizeApplicationStatus = (status) => {
  const normalized = (status || 'new').toString().trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (normalized === 'processing' || normalized === 'in_processing') return 'processing';
  if (normalized === 'closed') return 'closed';
  if (normalized === 'rejected') return 'rejected';
  return 'new';
};

const normalizeApplicationRecord = (application) => ({
  ...application,
  status: normalizeApplicationStatus(application.status),
});

const normalizeApplicationRecords = (records = []) => {
  const serialById = new Map(
    [...records]
      .sort((a, b) => {
        const at = a.submitted_at ? new Date(a.submitted_at).getTime() : 0;
        const bt = b.submitted_at ? new Date(b.submitted_at).getTime() : 0;
        return at !== bt ? at - bt : String(a.id || '').localeCompare(String(b.id || ''));
      })
      .map((app, i) => [app.id, `APP${String(i + 1).padStart(4, '0')}`])
  );

  return records.map((app) =>
    normalizeApplicationRecord({
      ...app,
      fjs_id: app.fjs_id || serialById.get(app.id) || '',
    })
  );
};

const formatStatusLabel = (status) => {
  const s = normalizeApplicationStatus(status);
  if (s === 'processing') return 'In Processing';
  if (s === 'closed') return 'Closed';
  if (s === 'rejected') return 'Rejected';
  return 'New';
};

const csvEscape = (value) => {
  const text = String(value ?? '').replace(/\r?\n/g, ' ');
  return `"${text.replace(/"/g, '""')}"`;
};

const getApplicationServiceName = (application) => (
  application.service_interest || application.job || 'General application'
);

const getApplicationFieldGroups = (application) => (
  supportApplicationFieldGroups[getApplicationServiceName(application)] || applicationFieldGroups
);

const getApplicationDocumentLinks = (application) => (
  supportDocumentLinks[getApplicationServiceName(application)] || documentLinks
);

/* ─── SVG Icons ──────────────────────────────────────────── */

const IconDashboard = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M0 2a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm9 0a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-3a2 2 0 0 1-2-2V2zM0 9a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V9zm9 0a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-3a2 2 0 0 1-2-2V9z" />
  </svg>
);

const IconBriefcase = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M6.5 1A1.5 1.5 0 0 0 5 2.5V3H1.5A1.5 1.5 0 0 0 0 4.5v8A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5v-8A1.5 1.5 0 0 0 14.5 3H11v-.5A1.5 1.5 0 0 0 9.5 1h-3zm0 1h3a.5.5 0 0 1 .5.5V3H6v-.5a.5.5 0 0 1 .5-.5zm1.886 6.914L15 7.151V12.5a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5V7.15l6.614 1.764a1.5 1.5 0 0 0 .772 0zM1.5 4h13a.5.5 0 0 1 .5.5v1.616L8.129 7.948a.5.5 0 0 1-.258 0L1 6.116V4.5a.5.5 0 0 1 .5-.5z" />
  </svg>
);

const IconPeople = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
    <path fillRule="evenodd" d="M5.216 14A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216z" />
    <path d="M4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />
  </svg>
);

const IconTag = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M2 2a1 1 0 0 1 1-1h4.586a1 1 0 0 1 .707.293l7 7a1 1 0 0 1 0 1.414l-4.586 4.586a1 1 0 0 1-1.414 0l-7-7A1 1 0 0 1 2 6.586V2zm3.5 4a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />
  </svg>
);

const IconPlus = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14" aria-hidden="true">
    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
  </svg>
);

const IconChevron = ({ className }) => (
  <svg className={`admin-nav-chevron ${className || ''}`} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
  </svg>
);

const IconBuilding = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M14.763.075A.5.5 0 0 1 15 .5v15a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5V14h-1v1.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V10a.5.5 0 0 1 .342-.474L6 7.64V4.5a.5.5 0 0 1 .276-.447l8-4a.5.5 0 0 1 .487.022zM6 8.694L1 10.36V15h5V8.694zM7 15h2v-1.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5V15h2V1.309l-7 3.5V15z" />
    <path d="M2 11h1v1H2v-1zm2 0h1v1H4v-1zm-2 2h1v1H2v-1zm2 0h1v1H4v-1zm4-4h1v1H8V9zm2 0h1v1h-1V9zm-2 2h1v1H8v-1zm2 0h1v1h-1v-1zm2-2h1v1h-1V9zm0 2h1v1h-1v-1zM8 7h1v1H8V7zm2 0h1v1h-1V7zm2 0h1v1h-1V7zM8 5h1v1H8V5zm2 0h1v1h-1V5zm2 0h1v1h-1V5zm0-2h1v1h-1V3z" />
  </svg>
);

/* ─── Component ──────────────────────────────────────────── */

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });

  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [jobForm, setJobForm] = useState(initialJobForm);
  const [jobMessage, setJobMessage] = useState('');
  const [appMessage, setAppMessage] = useState('');
  const [appMessageTone, setAppMessageTone] = useState('info');
  const [busy, setBusy] = useState(false);
  const [jobBusyId, setJobBusyId] = useState(null);
  const [editingJobId, setEditingJobId] = useState(null);

  const [activeSection, setActiveSection] = useState('overview');
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [statusSavingId, setStatusSavingId] = useState(null);
  const [applicationSearch, setApplicationSearch] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');

  // Submenu open state
  const [jobsOpen, setJobsOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(true);
  const [svcOpen, setSvcOpen] = useState(false);

  /* ─── API ──────────────────────────────────────────────── */

  const fetchWithSession = (url, options = {}) =>
    fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
      ...options,
    });

  const loadAdminData = async () => {
    const [jobsRes, appsRes] = await Promise.all([
      fetchWithSession(`${apiBaseUrl}/admin/jobs/`),
      fetchWithSession(`${apiBaseUrl}/admin/applications/`),
    ]);
    const jobsResult = await parseApiResponse(jobsRes);
    const appsResult = await parseApiResponse(appsRes);
    if (jobsRes.ok) setJobs(jobsResult.results || []);
    if (appsRes.ok) setApplications(normalizeApplicationRecords(appsResult.results || []));
  };

  useEffect(() => {
    let active = true;
    const checkSession = async () => {
      try {
        const res = await fetchWithSession(`${apiBaseUrl}/admin/me/`, { headers: {} });
        const result = await parseApiResponse(res);
        if (!active) return;
        if (res.ok && result.user) {
          setUser(result.user);
          await loadAdminData();
        } else {
          setUser(null);
        }
      } catch {
        if (active) setAuthError('Could not reach the backend admin API.');
      } finally {
        if (active) setAuthLoading(false);
      }
    };
    checkSession();
    return () => { active = false; };
  }, []);

  /* ─── Auth ─────────────────────────────────────────────── */

  const handleLogin = async (event) => {
    event.preventDefault();
    setBusy(true);
    setAuthError('');
    try {
      const res = await fetchWithSession(`${apiBaseUrl}/admin/login/`, {
        method: 'POST',
        body: JSON.stringify(loginForm),
      });
      const result = await parseApiResponse(res);
      if (!res.ok) throw new Error(result.detail || 'Could not log in.');
      setUser(result.user);
      await loadAdminData();
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    await fetchWithSession(`${apiBaseUrl}/admin/logout/`, { method: 'POST', body: JSON.stringify({}) });
    setUser(null);
    setJobs([]);
    setApplications([]);
  };

  /* ─── Jobs ─────────────────────────────────────────────── */

  const handleCreateJob = async (event) => {
    event.preventDefault();
    setBusy(true);
    setJobMessage('');
    try {
      const isEditing = Boolean(editingJobId);
      const res = await fetchWithSession(
        isEditing ? `${apiBaseUrl}/admin/jobs/${editingJobId}/` : `${apiBaseUrl}/admin/jobs/`,
        { method: isEditing ? 'PATCH' : 'POST', body: JSON.stringify(jobForm) }
      );
      const result = await parseApiResponse(res);
      if (!res.ok) throw new Error(result.detail || `Could not ${isEditing ? 'update' : 'create'} job.`);
      setJobForm(initialJobForm);
      setEditingJobId(null);
      setJobMessage(`Job ${isEditing ? 'updated' : 'created'}: ${result.title}`);
      setActiveSection('jobs');
      await loadAdminData();
    } catch (error) {
      setJobMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleEditJob = (job) => {
    setEditingJobId(job.id);
    setJobForm({
      title: job.title || '',
      department: job.department || '',
      location: job.location || 'Bengaluru',
      employment_type: job.employment_type || 'Full Time',
      experience: job.experience || '',
      summary: job.summary || '',
      responsibilities: job.responsibilities || '',
      requirements: job.requirements || '',
      status: job.status || 'draft',
    });
    setJobMessage('');
    setJobsOpen(true);
    setActiveSection('create-job');
  };

  const handleDeleteJob = async (job) => {
    if (!window.confirm(`Delete job posting "${job.title}"?`)) return;
    setJobBusyId(job.id);
    setJobMessage('');
    setAppMessage('');
    try {
      const res = await fetchWithSession(`${apiBaseUrl}/admin/jobs/${job.id}/`, {
        method: 'DELETE',
        body: JSON.stringify({}),
      });
      const result = await parseApiResponse(res);
      if (!res.ok) throw new Error(result.detail || 'Could not delete job.');
      setJobs((current) => current.filter((item) => item.id !== job.id));
      await loadAdminData();
      if (editingJobId === job.id) { setEditingJobId(null); setJobForm(initialJobForm); }
      setJobMessage(`Job deleted: ${job.title}`);
      setAppMessageTone('success');
      setAppMessage(`Job deleted: ${job.title}`);
    } catch (error) {
      setJobMessage(error.message);
      setAppMessageTone('error');
      setAppMessage(error.message);
    } finally {
      setJobBusyId(null);
    }
  };

  /* ─── Applications ─────────────────────────────────────── */

  const handleApplicationUpdate = async (applicationId, payload) => {
    setAppMessage('');
    setAppMessageTone('info');
    try {
      const normalizedPayload = {
        ...payload,
        ...(payload.status ? { status: normalizeApplicationStatus(payload.status) } : {}),
      };
      const res = await fetchWithSession(`${apiBaseUrl}/admin/applications/${applicationId}/`, {
        method: 'PATCH',
        body: JSON.stringify(normalizedPayload),
      });
      const result = await parseApiResponse(res);
      if (!res.ok) throw new Error(result.detail || 'Could not update application.');

      setApplications((current) =>
        normalizeApplicationRecords(current.map((app) => (app.id === applicationId ? result : app)))
      );

      if (payload.status && activeSection.startsWith('applications-')) {
        const currentFilter = getApplicationFilter();
        const nextStatus = normalizeApplicationStatus(payload.status);
        if (currentFilter && currentFilter !== nextStatus) {
          setActiveSection(`applications-${nextStatus}`);
          setAppMessageTone('success');
          setAppMessage(`Moved ${result.full_name} to ${getSectionLabel(`applications-${nextStatus}`)}.`);
          return;
        }
      }
      setAppMessageTone('success');
      setAppMessage(`Updated: ${result.full_name}`);
    } catch (error) {
      setAppMessageTone('error');
      setAppMessage(
        error.message === 'Failed to fetch'
          ? 'Could not reach the backend. Restart Django and make sure you are using the same host.'
          : error.message
      );
    }
  };

  const requestStatusChange = (application, nextStatus) => {
    const normalizedNext = normalizeApplicationStatus(nextStatus);
    const current = normalizeApplicationStatus(application.status);
    if (normalizedNext === current) return;
    setPendingStatusChange({
      applicationId: application.id,
      fullName: application.full_name,
      fromStatus: current,
      toStatus: normalizedNext,
    });
  };

  const confirmStatusChange = async () => {
    if (!pendingStatusChange) return;
    setStatusSavingId(pendingStatusChange.applicationId);
    await handleApplicationUpdate(pendingStatusChange.applicationId, { status: pendingStatusChange.toStatus });
    setStatusSavingId(null);
    setPendingStatusChange(null);
  };

  const handleDownloadApplications = () => {
    const rows = [
      exportColumns.map(([label]) => csvEscape(label)).join(','),
      ...filteredApplications.map((app) =>
        exportColumns.map(([, key]) => csvEscape(app[key])).join(',')
      ),
    ];
    const csv = `\uFEFF${rows.join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `laftek-applications-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  /* ─── Auto-clear toast ─────────────────────────────────── */

  useEffect(() => {
    if (!appMessage) return undefined;
    const id = window.setTimeout(() => setAppMessage(''), 3200);
    return () => window.clearTimeout(id);
  }, [appMessage]);

  /* ─── Derived data ─────────────────────────────────────── */

  const getApplicationFilter = () => {
    if (!activeSection.startsWith('applications-')) return null;
    const key = activeSection.replace('applications-', '');
    return key === 'all' ? null : key;
  };

  const getSectionLabel = (key) => {
    const sec = adminSections.find((s) => s.key === key);
    return sec ? sec.label : 'Applications';
  };

  const dashboardStats = useMemo(() => {
    const openJobs = jobs.filter((j) => j.status === 'open').length;
    const draftJobs = jobs.filter((j) => j.status === 'draft').length;
    const newApps = applications.filter((a) => normalizeApplicationStatus(a.status) === 'new').length;
    const processing = applications.filter((a) => normalizeApplicationStatus(a.status) === 'processing').length;
    return [
      { label: 'Open Jobs', value: openJobs },
      { label: 'Draft Jobs', value: draftJobs },
      { label: 'New Applications', value: newApps },
      { label: 'Processing', value: processing },
    ];
  }, [applications, jobs]);

  const appStatusCounts = useMemo(() => ({
    all: applications.length,
    new: applications.filter((a) => normalizeApplicationStatus(a.status) === 'new').length,
    processing: applications.filter((a) => normalizeApplicationStatus(a.status) === 'processing').length,
    closed: applications.filter((a) => normalizeApplicationStatus(a.status) === 'closed').length,
    rejected: applications.filter((a) => normalizeApplicationStatus(a.status) === 'rejected').length,
  }), [applications]);

  const filteredApplications = useMemo(() => {
    const statusFilter = getApplicationFilter();
    const search = applicationSearch.trim().toLowerCase();
    return applications.filter((app) => {
      const matchesStatus = !statusFilter || normalizeApplicationStatus(app.status) === statusFilter;
      const serviceName = app.job || app.service_interest || 'General application';
      const matchesService = serviceFilter === 'all' || serviceName === serviceFilter;
      const matchesSearch = !search || [
        app.full_name, app.fjs_id, app.phone_number, app.alternate_phone_number,
        app.email, serviceName, app.current_location, app.skills_trained,
        app.consultant_name, app.company_name, app.role, app.pan_number,
        app.bank_name, app.bank_account_number, app.technology_course_name,
      ].some((v) => String(v || '').toLowerCase().includes(search));
      return matchesStatus && matchesService && matchesSearch;
    });
  }, [activeSection, applicationSearch, applications, serviceFilter]);

  const applicationServices = useMemo(() => {
    const fromApps = applications.map((a) => a.job || a.service_interest || 'General application').filter(Boolean);
    const fromServices = services.map((s) => s.title);
    return [...new Set([...fromServices, ...fromApps])].sort((a, b) => a.localeCompare(b));
  }, [applications]);

  const serviceStatusStats = useMemo(() => applicationServices.map((name) => {
    const list = applications.filter((a) => (a.job || a.service_interest || 'General application') === name);
    return {
      serviceName: name,
      total: list.length,
      new: list.filter((a) => normalizeApplicationStatus(a.status) === 'new').length,
      processing: list.filter((a) => normalizeApplicationStatus(a.status) === 'processing').length,
      closed: list.filter((a) => normalizeApplicationStatus(a.status) === 'closed').length,
      rejected: list.filter((a) => normalizeApplicationStatus(a.status) === 'rejected').length,
    };
  }), [applicationServices, applications]);

  /* ─── Helpers: Topbar breadcrumb ───────────────────────── */

  const getBreadcrumb = () => {
    if (activeSection === 'overview') return { parent: 'Dashboard', current: 'Overview' };
    if (activeSection === 'jobs' || activeSection === 'create-job') return { parent: 'Jobs', current: activeSection === 'jobs' ? 'All postings' : editingJobId ? 'Edit job' : 'Create new' };
    return { parent: 'Applications', current: appSubLabel(activeSection) };
  };

  const appSubLabel = (key) => {
    if (key === 'applications-all') return 'All';
    if (key === 'applications-new') return 'New';
    if (key === 'applications-processing') return 'Processing';
    if (key === 'applications-closed') return 'Closed';
    if (key === 'applications-rejected') return 'Rejected';
    return 'Applications';
  };

  /* ─── Render: Status select ────────────────────────────── */

  const renderStatusSelect = (application) => {
    const status = normalizeApplicationStatus(application.status);
    return (
      <div className="admin-status-control">
        <span className={`admin-status admin-status-${status}`}>{formatStatusLabel(status)}</span>
        <select
          value={status}
          onChange={(e) => requestStatusChange(application, e.target.value)}
          className={`admin-inline-status admin-inline-status-${status}`}
          disabled={statusSavingId === application.id}
          aria-label={`Change status for ${application.full_name}`}
        >
          <option value="new">New</option>
          <option value="processing">In Processing</option>
          <option value="closed">Closed</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
    );
  };

  /* ─── Loading / Login ──────────────────────────────────── */

  if (authLoading) {
    return (
      <section className="admin-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#57606a', fontSize: 14 }}>Loading admin…</p>
      </section>
    );
  }

  if (!user) {
    return (
      <section className="admin-shell">
        <div className="admin-login-card">
          <h1>Admin Login</h1>
          <p className="admin-copy">Sign in to manage jobs and applications.</p>
          {authError ? <div className="admin-message admin-message-error" style={{ marginTop: 12 }}>{authError}</div> : null}
          <form className="admin-login-form" onSubmit={handleLogin}>
            <label>
              Username
              <input
                value={loginForm.username}
                onChange={(e) => setLoginForm((c) => ({ ...c, username: e.target.value }))}
                required
                autoComplete="username"
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm((c) => ({ ...c, password: e.target.value }))}
                required
                autoComplete="current-password"
              />
            </label>
            <button type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
          </form>
        </div>
      </section>
    );
  }

  const breadcrumb = getBreadcrumb();

  /* ─── Main Render ──────────────────────────────────────── */

  return (
    <section className="admin-shell">

      {/* Toast */}
      {appMessage ? (
        <div className={`admin-toast admin-toast-${appMessageTone}`}>
          <strong>{appMessageTone === 'error' ? 'Error' : 'Saved'}</strong>
          <p>{appMessage}</p>
        </div>
      ) : null}

      <div className="admin-dashboard">

        {/* ── Sidebar ── */}
        <aside className="admin-sidebar">

          {/* Brand */}
          <div className="admin-sidebar-brand">
            <div className="admin-sidebar-brand-icon">
              <IconBuilding />
            </div>
            <h2>Laftek Panel</h2>
          </div>

          {/* Nav */}
          <nav className="admin-sidebar-nav" aria-label="Admin navigation">
            <div className="admin-nav-section-label">Main</div>

            {/* Overview */}
            <button
              type="button"
              className={`admin-nav-item ${activeSection === 'overview' ? 'active' : ''}`}
              onClick={() => { setActiveSection('overview'); setServiceFilter('all'); }}
            >
              <IconDashboard /> Overview
            </button>

            {/* Jobs group */}
            <div className="admin-nav-section-label">Jobs</div>
            <button
              type="button"
              className={`admin-nav-item ${(activeSection === 'jobs' || activeSection === 'create-job') && !jobsOpen ? 'active' : ''}`}
              onClick={() => setJobsOpen((o) => !o)}
            >
              <IconBriefcase />
              Jobs
              <span className="admin-nav-badge">{jobs.length}</span>
              <IconChevron className={jobsOpen ? 'open' : ''} />
            </button>
            <div className={`admin-sub-nav ${jobsOpen ? 'open' : ''}`}>
              <button
                type="button"
                className={`admin-sub-item ${activeSection === 'jobs' ? 'active' : ''}`}
                onClick={() => setActiveSection('jobs')}
              >
                <span className="admin-sub-dot" /> All postings
                <span className="admin-sub-count">{jobs.length}</span>
              </button>
              <button
                type="button"
                className={`admin-sub-item ${activeSection === 'create-job' ? 'active' : ''}`}
                onClick={() => {
                  setEditingJobId(null);
                  setJobForm(initialJobForm);
                  setJobMessage('');
                  setActiveSection('create-job');
                }}
              >
                <span className="admin-sub-dot" /> Create new
              </button>
            </div>

            {/* Applications group */}
            <div className="admin-nav-section-label">Applications</div>
            <button
              type="button"
              className={`admin-nav-item ${activeSection.startsWith('applications-') && !appsOpen ? 'active' : ''}`}
              onClick={() => setAppsOpen((o) => !o)}
            >
              <IconPeople />
              Applications
              <span className="admin-nav-badge">{applications.length}</span>
              <IconChevron className={appsOpen ? 'open' : ''} />
            </button>
            <div className={`admin-sub-nav ${appsOpen ? 'open' : ''}`}>
              {[
                { key: 'applications-all', label: 'All', count: appStatusCounts.all },
                { key: 'applications-new', label: 'New', count: appStatusCounts.new },
                { key: 'applications-processing', label: 'Processing', count: appStatusCounts.processing },
                { key: 'applications-closed', label: 'Closed', count: appStatusCounts.closed },
                { key: 'applications-rejected', label: 'Rejected', count: appStatusCounts.rejected },
              ].map(({ key, label, count }) => (
                <button
                  key={key}
                  type="button"
                  className={`admin-sub-item ${activeSection === key ? 'active' : ''}`}
                  onClick={() => { setActiveSection(key); setServiceFilter('all'); }}
                >
                  <span className="admin-sub-dot" /> {label}
                  <span className="admin-sub-count">{count}</span>
                </button>
              ))}
            </div>

            {/* Support Categories group */}
            <div className="admin-nav-section-label">Support</div>
            <button
              type="button"
              className="admin-nav-item"
              onClick={() => setSvcOpen((o) => !o)}
            >
              <IconTag />
              Categories
              <IconChevron className={svcOpen ? 'open' : ''} />
            </button>
            <div className={`admin-sub-nav ${svcOpen ? 'open' : ''}`}>
              {applicationServices.map((name) => (
                <button
                  key={name}
                  type="button"
                  className={`admin-sub-item ${serviceFilter === name ? 'active' : ''}`}
                  onClick={() => { setActiveSection('applications-all'); setServiceFilter(name); }}
                >
                  <span className="admin-sub-dot" /> {name}
                </button>
              ))}
            </div>
          </nav>


          {/* User footer */}
          <div className="admin-sidebar-user">
            <div className="admin-sidebar-user-avatar">
              {user.username ? user.username.slice(0, 1).toUpperCase() : 'A'}
            </div>
            <div className="admin-sidebar-user-copy">
              <strong>{user.username}</strong>
              <p>{user.role}{user.designation ? ` · ${user.designation}` : ''}</p>
            </div>
          </div>
        </aside>

        {/* ── Main ── */}
        <div className="admin-main">

          {/* Topbar */}
          <div className="admin-topbar">
            <div className="admin-topbar-left">
              <span className="admin-breadcrumb">{breadcrumb.parent}</span>
              <span className="admin-breadcrumb-sep">/</span>
              <h1>{breadcrumb.current}</h1>
            </div>
            <div className="admin-topbar-actions">
              <a href="/home" className="admin-btn admin-link-button">← Back to site</a>
              <button type="button" className="admin-btn admin-ghost-button" onClick={handleLogout}>Logout</button>
              <button
                type="button"
                className="admin-btn admin-btn-info"
                onClick={() => {
                  setEditingJobId(null);
                  setJobForm(initialJobForm);
                  setJobMessage('');
                  setJobsOpen(true);
                  setActiveSection('create-job');
                }}
              >
                <IconPlus /> New job
              </button>
            </div>
          </div>

          {/* ── Overview ── */}
          {activeSection === 'overview' ? (
            <div className="admin-section">
              <div className="admin-page-header">
                <div>
                  <p className="admin-kicker">Control Center</p>
                  <h2 style={{ margin: 0 }}>Welcome back, {user.username}</h2>
                </div>
              </div>

              <div className="admin-stats-grid">
                {dashboardStats.map((item) => (
                  <div key={item.label} className="admin-stat-card">
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>

              <div className="admin-grid">
                <div className="admin-card">
                  <h2>
                    Recent jobs
                    <button type="button" className="admin-card-header-link" onClick={() => setActiveSection('jobs')}>
                      View all →
                    </button>
                  </h2>
                  <div className="admin-list">
                    {jobs.slice(0, 5).map((job) => (
                      <div key={job.id} className="admin-list-item">
                        <div className="admin-list-item-icon blue"><IconBriefcase /></div>
                        <div className="admin-list-item-body">
                          <strong>{job.title}</strong>
                          <p>{job.department || 'General'} · {job.location}</p>
                        </div>
                        <span className={`admin-status admin-status-${job.status}`}>{job.status}</span>
                      </div>
                    ))}
                    {!jobs.length ? <p className="admin-empty">No jobs yet.</p> : null}
                  </div>
                </div>

                <div className="admin-card">
                  <h2>
                    Latest applications
                    <button type="button" className="admin-card-header-link" onClick={() => setActiveSection('applications-all')}>
                      View all →
                    </button>
                  </h2>
                  <div className="admin-list">
                    {applications.slice(0, 5).map((app) => (
                      <div key={app.id} className="admin-list-item">
                        <div className="admin-list-item-icon blue"><IconPeople /></div>
                        <div className="admin-list-item-body">
                          <strong>{app.full_name}</strong>
                          <p>{app.fjs_id || '—'} · {app.job || app.service_interest || 'General'}</p>
                        </div>
                        <span className={`admin-status admin-status-${normalizeApplicationStatus(app.status)}`}>
                          {formatStatusLabel(app.status)}
                        </span>
                      </div>
                    ))}
                    {!applications.length ? <p className="admin-empty">No applications yet.</p> : null}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* ── Create / Edit Job ── */}
          {activeSection === 'create-job' ? (
            <div className="admin-section">
              <div className="admin-page-header">
                <div>
                  <h2>{editingJobId ? 'Edit job posting' : 'Create job posting'}</h2>
                  <p className="admin-copy">
                    {editingJobId
                      ? 'Update the selected job posting and save your changes.'
                      : 'New jobs appear on the public jobs page when status is set to "open".'}
                  </p>
                </div>
              </div>
              <div className="admin-card">
                {jobMessage ? <div className="admin-message">{jobMessage}</div> : null}
                <form className="admin-job-form" onSubmit={handleCreateJob}>
                  <div className="admin-form-row">
                    <label>Title <input value={jobForm.title} onChange={(e) => setJobForm((c) => ({ ...c, title: e.target.value }))} required /></label>
                    <label>Department <input value={jobForm.department} onChange={(e) => setJobForm((c) => ({ ...c, department: e.target.value }))} /></label>
                  </div>
                  <div className="admin-form-row">
                    <label>Location <input value={jobForm.location} onChange={(e) => setJobForm((c) => ({ ...c, location: e.target.value }))} /></label>
                    <label>Experience <input value={jobForm.experience} onChange={(e) => setJobForm((c) => ({ ...c, experience: e.target.value }))} /></label>
                  </div>
                  <div className="admin-form-row">
                    <label>Employment type
                      <input value={jobForm.employment_type} onChange={(e) => setJobForm((c) => ({ ...c, employment_type: e.target.value }))} />
                    </label>
                    <label>Status
                      <select value={jobForm.status} onChange={(e) => setJobForm((c) => ({ ...c, status: e.target.value }))}>
                        <option value="draft">Draft</option>
                        <option value="open">Open</option>
                        <option value="closed">Closed</option>
                      </select>
                    </label>
                  </div>
                  <label>Summary <textarea value={jobForm.summary} onChange={(e) => setJobForm((c) => ({ ...c, summary: e.target.value }))} required /></label>
                  <label>Responsibilities <textarea value={jobForm.responsibilities} onChange={(e) => setJobForm((c) => ({ ...c, responsibilities: e.target.value }))} /></label>
                  <label>Requirements <textarea value={jobForm.requirements} onChange={(e) => setJobForm((c) => ({ ...c, requirements: e.target.value }))} /></label>
                  <div className="admin-job-form-actions">
                    <button type="submit" className="admin-btn-primary" disabled={busy}>
                      {busy ? 'Saving…' : editingJobId ? 'Update job' : 'Create job'}
                    </button>
                    {editingJobId ? (
                      <button
                        type="button"
                        className="admin-btn admin-ghost-button"
                        onClick={() => { setEditingJobId(null); setJobForm(initialJobForm); setJobMessage(''); }}
                      >
                        Cancel
                      </button>
                    ) : null}
                  </div>
                </form>
              </div>
            </div>
          ) : null}

          {/* ── Job Postings ── */}
          {activeSection === 'jobs' ? (
            <div className="admin-section">
              <div className="admin-page-header">
                <div>
                  <h2>Job postings</h2>
                  <p className="admin-copy">{jobs.length} total job{jobs.length !== 1 ? 's' : ''}</p>
                </div>
                <button
                  type="button"
                  className="admin-btn admin-btn-info"
                  onClick={() => {
                    setEditingJobId(null);
                    setJobForm(initialJobForm);
                    setJobMessage('');
                    setActiveSection('create-job');
                  }}
                >
                  <IconPlus /> Create job
                </button>
              </div>
              <div className="admin-card">
                {jobMessage ? <div className="admin-message">{jobMessage}</div> : null}
                <div className="admin-list">
                  {jobs.map((job) => (
                    <div key={job.id} className="admin-list-item">
                      <div className="admin-list-item-icon blue"><IconBriefcase /></div>
                      <div className="admin-list-item-body">
                        <strong>{job.title}</strong>
                        <p>{job.department || 'General'} · {job.location} · {job.experience || 'Experience not specified'}</p>
                      </div>
                      <div className="admin-job-actions">
                        <span className={`admin-status admin-status-${job.status}`}>{job.status}</span>
                        <button type="button" className="admin-btn admin-ghost-button" style={{ padding: '3px 10px', minHeight: 28, fontSize: 12 }} onClick={() => handleEditJob(job)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-danger"
                          style={{ padding: '3px 10px', minHeight: 28, fontSize: 12 }}
                          onClick={() => handleDeleteJob(job)}
                          disabled={jobBusyId === job.id}
                        >
                          {jobBusyId === job.id ? '…' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  ))}
                  {!jobs.length ? <p className="admin-empty">No jobs yet. Create your first job posting.</p> : null}
                </div>
              </div>
            </div>
          ) : null}

          {/* ── Applications ── */}
          {activeSection.startsWith('applications-') ? (
            <div className="admin-section">
              <div className="admin-page-header">
                <div>
                  <h2>Applications</h2>
                  <p className="admin-copy">Review submissions, update status, and keep internal notes.</p>
                </div>
                <button
                  type="button"
                  className="admin-download-button"
                  onClick={handleDownloadApplications}
                  disabled={!filteredApplications.length}
                >
                  ↓ Export CSV
                </button>
              </div>

              {/* Toolbar */}
              <div className="admin-table-toolbar">
                <div className="admin-application-tools">
                  <label className="admin-search-field">
                    <span>Search</span>
                    <input
                      value={applicationSearch}
                      onChange={(e) => setApplicationSearch(e.target.value)}
                      placeholder="Name, ID, phone, email, skill…"
                    />
                  </label>
                  <label className="admin-filter-field">
                    <span>Status</span>
                    <select
                      value={activeSection.replace('applications-', '')}
                      onChange={(e) => setActiveSection(`applications-${e.target.value}`)}
                    >
                      <option value="all">All</option>
                      <option value="new">New</option>
                      <option value="processing">Processing</option>
                      <option value="closed">Closed</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </label>
                  <label className="admin-filter-field">
                    <span>Category</span>
                    <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
                      <option value="all">All categories</option>
                      {applicationServices.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="admin-results-summary">
                  Showing <strong>{filteredApplications.length}</strong> of <strong>{applications.length}</strong> applications
                </div>
              </div>

              {/* Application cards */}
              <div className="admin-issue-list">
                {filteredApplications.map((app) => (
                  <article key={app.id} className="admin-issue-card">
                    <div className="admin-issue-main">
                      <div className="admin-issue-header">
                        <div>
                          <div className="admin-issue-title-row">
                            <h3>{app.full_name}</h3>
                            {app.fjs_id ? <span className="admin-fjs-id">{app.fjs_id}</span> : null}
                          </div>
                          <p>{app.job || app.service_interest || 'General application'}</p>
                        </div>
                        {renderStatusSelect(app)}
                      </div>

                      <div className="admin-issue-groups">
                        {getApplicationFieldGroups(app).map((group) => {
                          const visible = group.fields
                            .map(([label, key]) => [label, app[key]]);
                          if (!visible.length) return null;
                          return (
                            <section key={group.title} className="admin-issue-group">
                              <h4>{group.title}</h4>
                              <div className="admin-issue-field-grid">
                                {visible.map(([label, val]) => (
                                  <div key={label} className="admin-issue-field">
                                    <span>{label}</span>
                                    <strong>{val || '-'}</strong>
                                  </div>
                                ))}
                              </div>
                            </section>
                          );
                        })}
                      </div>

                      <div className="admin-issue-documents">
                        {getApplicationDocumentLinks(app).map(([label, key]) => (
                          app[key] ? (
                            <a key={key} href={app[key]} target="_blank" rel="noreferrer">{label}</a>
                          ) : (
                            <span key={key}>{label}: Not uploaded</span>
                          )
                        ))}
                      </div>
                    </div>

                    <aside className="admin-issue-notes">
                      <label>
                        Admin notes
                        <textarea
                          value={app.admin_notes || ''}
                          onChange={(e) =>
                            setApplications((current) =>
                              current.map((item) =>
                                item.id === app.id ? { ...item, admin_notes: e.target.value } : item
                              )
                            )
                          }
                          placeholder="Add internal note…"
                          className="admin-inline-notes"
                        />
                      </label>
                      <button
                        type="button"
                        className="admin-row-save"
                        onClick={() => handleApplicationUpdate(app.id, { admin_notes: app.admin_notes || '' })}
                      >
                        Save note
                      </button>
                    </aside>
                  </article>
                ))}
              </div>

              {!filteredApplications.length ? (
                <p className="admin-empty">No applications match the current filters.</p>
              ) : null}
            </div>
          ) : null}

        </div>
      </div>

      {/* ── Confirm Modal ── */}
      {pendingStatusChange ? (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onClick={() => setPendingStatusChange(null)}
        >
          <div
            className="admin-modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="admin-kicker">Confirmation</p>
            <h2 id="confirm-modal-title">Confirm status change</h2>
            <p className="admin-copy">
              Move <strong>{pendingStatusChange.fullName}</strong> from{' '}
              <strong>{pendingStatusChange.fromStatus.replace('_', ' ')}</strong> to{' '}
              <strong>{pendingStatusChange.toStatus.replace('_', ' ')}</strong>?
            </p>
            <div className="admin-confirm-actions">
              <button
                type="button"
                className="admin-btn admin-ghost-button"
                onClick={() => setPendingStatusChange(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                onClick={confirmStatusChange}
                disabled={statusSavingId === pendingStatusChange.applicationId}
              >
                {statusSavingId === pendingStatusChange.applicationId ? 'Updating…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
